import { assertValue } from '@libs/utils';

import {
  PriceCalendarTileType,
  TileSelectionType,
  isCurrentMonthAvailableDayTile,
  isCurrentMonthTile,
} from '../models';
import type { CurrentMonthTile, PriceCalendarTile } from '../models';

interface PriceCalendarStateConstructorParams {
  // NOTE: Date in a month for which we want to have calendar
  monthDate: Date;
  // NOTE: Translated weekday labels in correct order (locale based) i.e. ["Sun", "Mon", ...]
  weekdays: string[];
  // NOTE: This is used to format calendar properly. This must match whatever is inside weekdays
  // 0: 'Sunday',
  // 1: 'Monday',
  // 2: 'Tuesday',
  // 3: 'Wednesday',
  // 4: 'Thursday',
  // 5: 'Friday',
  // 6: 'Saturday',
  firstWeekdayJsIndex: number;
  // NOTE: Today date, used to decide, if calendar tile should be current month loader or current month past tile
  today: Date;
}

export class PriceCalendarState {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static daysInAWeek = 7;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static shortMonthWeeks = 6;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static longMonthWeeks = 7;
  private calendarMatrix: PriceCalendarTile[][] = [[], [], [], [], [], [], []];

  constructor(params: PriceCalendarStateConstructorParams) {
    this.initializeCalendarMatrix(params);
  }

  public getCalendarMatrix(): PriceCalendarTile[][] {
    return this.calendarMatrix;
  }

  public getCurrentMonthTiles(): CurrentMonthTile[] {
    return this.calendarMatrix.flat().filter(isCurrentMonthTile);
  }

  public getTotalTilesInColumn(): number {
    const lastRowIndex = 6;
    const lastRow = this.getRow(lastRowIndex);
    return lastRow.length > 0
      ? PriceCalendarState.longMonthWeeks
      : PriceCalendarState.shortMonthWeeks;
  }

  public getSelectedOrSuggestedDayIndexFromAvailableDaysOnly(): number {
    const allTiles = this.calendarMatrix.flat();

    const currentAvailableTiles = allTiles.filter(
      isCurrentMonthAvailableDayTile
    );

    const selectedTile = currentAvailableTiles.find(
      (tile) => tile.selection === TileSelectionType.Selected
    );

    if (selectedTile) {
      return selectedTile.date.getDate();
    }

    const suggestedTile = currentAvailableTiles.find(
      (tile) => tile.selection === TileSelectionType.Suggested
    );

    if (suggestedTile) {
      return suggestedTile.date.getDate();
    }

    return 0;
  }

  public setTileForDate(date: Date, tile: CurrentMonthTile): void {
    const targetDay = date.getDate();

    for (let row = 1; row < this.calendarMatrix.length; row++) {
      const currentRow = this.getRow(row);

      for (let i = 0; i < currentRow.length; i++) {
        const current = currentRow[i];
        assertValue(
          current,
          `PriceCalendarState.ts > setTileForDate > current`
        );

        if (
          isCurrentMonthTile(current) &&
          current.date.getDate() === targetDay &&
          current.date.getMonth() === date.getMonth() &&
          current.date.getFullYear() === date.getFullYear()
        ) {
          currentRow[i] = tile;
          return;
        }
      }
    }
  }

  private getRow(index: number): PriceCalendarTile[] {
    const row = this.calendarMatrix[index];
    assertValue(row, `PriceCalendarState.ts > getRow > row`);
    return row;
  }

  private initializeCalendarMatrix(
    params: PriceCalendarStateConstructorParams
  ): void {
    this.addWeekdayHeaders(params.weekdays);

    const year = params.monthDate.getFullYear();
    const month = params.monthDate.getMonth();
    const firstOfMonth = new Date(Date.UTC(year, month, 1));
    const totalDaysInMonth = new Date(Date.UTC(year, month + 1, 0)).getDate();

    const jsFirstDayIndex = firstOfMonth.getDay();
    const daysInWeek = PriceCalendarState.daysInAWeek;
    const offset =
      (jsFirstDayIndex - params.firstWeekdayJsIndex + daysInWeek) % daysInWeek;

    const currentRow = this.getRow(1);

    this.fillPreviousMonthDays(currentRow, offset);
    this.fillCurrentMonthDays(
      currentRow,
      year,
      month,
      totalDaysInMonth,
      daysInWeek,
      params.today
    );
    this.fillNextMonthDays(currentRow, daysInWeek);
  }

  private addWeekdayHeaders(weekdays: string[]): void {
    const weekRow = this.getRow(0);
    weekdays.forEach((weekday) => {
      weekRow.push({
        type: PriceCalendarTileType.WeekDay,
        text: weekday,
      });
    });
  }

  private fillPreviousMonthDays(row: PriceCalendarTile[], count: number): void {
    for (let i = 0; i < count; i++) {
      row.push({
        type: PriceCalendarTileType.PreviousMonthDay,
      });
    }
  }

  private fillCurrentMonthDays(
    currentRow: PriceCalendarTile[],
    year: number,
    month: number,
    totalDays: number,
    daysInWeek: number,
    todayDate: Date
  ): void {
    let currentWeekIndex = 1;

    for (let day = 1; day <= totalDays; day++) {
      const currentDate = new Date(Date.UTC(year, month, day));
      const tile =
        currentDate < todayDate
          ? ({
              type: PriceCalendarTileType.CurrentMonthPastDay,
              text: day.toString(),
              date: currentDate,
            } as const)
          : ({
              type: PriceCalendarTileType.CurrentMonthLoaderDay,
              text: day.toString(),
              date: currentDate,
            } as const);

      currentRow.push(tile);

      if (currentRow.length === daysInWeek) {
        currentWeekIndex++;
        if (currentWeekIndex === daysInWeek) {
          break;
        }
        currentRow = this.getRow(currentWeekIndex);
      }
    }
  }

  private fillNextMonthDays(
    row: PriceCalendarTile[],
    daysInWeek: number
  ): void {
    while (row.length > 0 && row.length < daysInWeek) {
      row.push({
        type: PriceCalendarTileType.NextMonthDay,
      });
    }
  }
}
